import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, updateDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useConfig } from '../contexts/ConfigContext';
import LoadingAnimation from './LoadingAnimation';

const StatusBadge = ({ status }) => {
  const map = {
    published: { bg: '#2a6041', label: 'Published' },
    draft: { bg: '#666', label: 'Draft' },
    scheduled: { bg: '#1a4f8a', label: 'Scheduled' },
    deleted: { bg: '#ccc', color: '#666', label: 'Deleted' }
  };
  const s = map[status] || map.published;
  return (
    <span style={{
      background: s.bg, color: s.color || 'white',
      fontSize: '0.68rem', padding: '2px 8px', borderRadius: '10px',
      textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, whiteSpace: 'nowrap'
    }}>
      {s.label}
    </span>
  );
};

const AdminReflections = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();

  const [loading, setLoading] = useState(true);
  const [reflections, setReflections] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/login', { state: { from: location.pathname } });
      } else if (config.configLoaded && config.adminEmails.length > 0 && !config.adminEmails.includes(user.email)) {
        navigate('/');
      } else if (config.configLoaded) {
        fetchReflections().then(() => setLoading(false));
      }
    });
    return () => unsubscribe();
  }, [config.configLoaded, config.adminEmails]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReflections = async () => {
    const snap = await getDocs(query(collection(db, 'reflections'), orderBy('created_at', 'desc')));
    setReflections(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reflection? It will be hidden from the website.')) return;
    await updateDoc(doc(db, 'reflections', id), { status: 'deleted' });
    await fetchReflections();
  };

  const handleRestore = async (id) => {
    await updateDoc(doc(db, 'reflections', id), { status: 'published' });
    await fetchReflections();
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const effectiveStatus = (item) => {
    if (item.status === 'scheduled' && item.scheduled_at) {
      const sat = item.scheduled_at.toDate ? item.scheduled_at.toDate() : new Date(item.scheduled_at);
      if (sat <= new Date()) return 'published';
    }
    return item.status || 'published';
  };

  const visibleReflections = showDeleted ? reflections : reflections.filter(r => r.status !== 'deleted');

  if (loading) return <div style={{ minHeight: '100vh' }}><LoadingAnimation /></div>;

  return (
    <div className="stt-admin-page">
      <a href="/admin" className="stt-admin-back">← Back to Admin</a>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
        <h1 className="stt-admin-title">Reflections</h1>
        <a href="/admin/reflections/new" className="btn stt-btn-primary">+ New Reflection</a>
      </div>
      <hr />

      <div className="stt-admin-card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <label style={{ fontSize: '0.82rem', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0 }}>
            <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />
            Show deleted
          </label>
        </div>
        {visibleReflections.length === 0 ? (
          <p style={{ color: '#888' }}>No reflections yet. Click "+ New Reflection" to write one.</p>
        ) : (
          <div className="stt-admin-list">
            {visibleReflections.map(r => (
              <div key={r.id} className={`stt-admin-list-row${r.status === 'deleted' ? ' deleted' : ''}`}>
                <div className="stt-admin-list-info">
                  <div className="stt-admin-list-title">
                    <a href={`/reflections/${r.id}`} target="_blank" rel="noopener noreferrer">
                      {r.title}
                    </a>
                  </div>
                  <div className="stt-admin-list-meta">
                    {r.author && <span>{r.author}</span>}
                    {r.author && r.created_at && <span>·</span>}
                    {r.created_at && <span>{formatDate(r.created_at)}</span>}
                    <StatusBadge status={effectiveStatus(r)} />
                  </div>
                </div>
                <div className="stt-admin-list-actions">
                  <a href={`/admin/reflections/${r.id}/edit`} className="btn btn-sm btn-outline-secondary">Edit</a>
                  {r.status === 'deleted' ? (
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleRestore(r.id)}>Restore</button>
                  ) : (
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(r.id)}>Delete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReflections;
